// src/components/RealPianoListener.js
import { useCallback, useEffect, useRef, useState } from "react";
import pitchfinder from "pitchfinder";
import confetti from "canvas-confetti";
import { normalizeNoteName } from "./VirtualPiano";

/* ─────────────────────────────────────────────
   PURE HELPERS (module-scope so they are stable)
   ───────────────────────────────────────────── */

const NAMES_SHARP = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const MIDI_MIN = 21;
const MIDI_MAX = 108;
const FUND_MIN = 55;
const FUND_MAX = 1400;

function hasOctave(note) {
  return /\d$/.test(String(note || ""));
}

function midiToNameOct(midi) {
  const i = Math.round(midi);
  const name = NAMES_SHARP[i % 12];
  const oct = Math.floor(i / 12) - 1;
  return { name, oct, full: `${name}${oct}` };
}

function freqToMidi(freq) {
  return 69 + 12 * Math.log2(freq / 440);
}

function midiToFreq(m) {
  return 440 * Math.pow(2, (m - 69) / 12);
}

function nameOctToMidi(nameOct) {
  const m = String(nameOct).match(/^([A-G])(#|b)?(\d+)$/i);
  if (!m) return null;
  let [, L, acc, octStr] = m;
  L = L.toUpperCase();
  let name = L + (acc || "");
  const flatToSharp = { Db: "C#", Eb: "D#", Gb: "F#", Ab: "G#", Bb: "A#" };
  if (name.length === 2 && name[1] === "b") name = flatToSharp[name] || name;
  const idx = NAMES_SHARP.indexOf(name);
  if (idx < 0) return null;
  const oct = parseInt(octStr, 10);
  return (oct + 1) * 12 + idx;
}

function safeMidiFromFreq(freq) {
  const m = Math.round(freqToMidi(freq));
  return Math.max(MIDI_MIN, Math.min(MIDI_MAX, m));
}

function foldHarmonicNearExpected(freq, expectedRaw) {
  if (!isFinite(freq) || freq <= 0) return null;
  let f = freq;

  if (expectedRaw && hasOctave(expectedRaw)) {
    const midi = nameOctToMidi(expectedRaw);
    if (midi != null) {
      const target = midiToFreq(midi);
      while (f > target * 1.7) f /= 2;
      while (f < target * 0.6) f *= 2;
      return f;
    }
  }

  while (f > FUND_MAX) f /= 2;
  while (f < FUND_MIN) f *= 2;
  return f;
}

function rms(buf) {
  let s = 0;
  for (let i = 0; i < buf.length; i++) s += buf[i] * buf[i];
  return Math.sqrt(s / buf.length);
}

/**
 * AMDF-based listener with:
 * - Correct sampleRate handling (mobile semitone issues)
 * - Band-pass (HPF 70 Hz / LPF 1.4 kHz)
 * - Harmonic folding toward expected octave
 * - Optional semitone bias learning
 *
 * Wrong-confirmation:
 * - Fires onWrongConfirmed ONLY when wrong is stable + clearly wrong
 * - Tuned to catch 1-semitone wrong notes (≈100 cents) by default
 */
export default function RealPianoListener({
  targetMelody = [],
  onComplete = () => {},
  onNoteMatched = () => {},
  onNoteDetected = () => {},
  onWrongConfirmed = () => {},

  startLabel = "Play Now",
  stopLabel = "Stop Listening",
  buttonClassName = "",
  buttonWrapperClassName = "",
  containerClassName = "w-full flex flex-col items-center",
  showMicHint = true,
  autoStart = false,
  hideStartButton = false,

  tuningBiasCents = null,
  enableAutoSemitoneBias = false,

  toleranceCents = 70,
  toleranceHz = 10,
  detectionSensitivity = 1.0,
  minRms = 0.010,

  requireSilenceBetweenNotes = true,
  silenceFactor = 0.65,
  silenceFramesRequired = 2,
  maxRearmWaitMs = 700,

  wrongStableFramesRequired = 2,
  wrongCooldownMs = 260,
  wrongCentsThreshold = 70,
  wrongHzThreshold = 7,
}) {
  const [isListening, setIsListening] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [, forceUpdate] = useState(0);
  const currentIndexRef = useRef(0);
  const matchedRef = useRef([]);

  const micStreamRef = useRef(null);
  const processorRef = useRef(null);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const hpRef = useRef(null);
  const lpRef = useRef(null);

  const detectPitchRef = useRef(null);
  const stableNoteRef = useRef(null);
  const stableCountRef = useRef(0);
  const requiredStableFramesRef = useRef(3);

  const needsRearmRef = useRef(false);
  const silenceFramesRef = useRef(0);
  const rearmStartedAtRef = useRef(0);

  const wrongNoteRef = useRef(null);
  const wrongCountRef = useRef(0);
  const lastWrongAtRef = useRef(0);

  const isMobileUA =
    typeof navigator !== "undefined" &&
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isSecure = typeof window !== "undefined" && window.isSecureContext;

  const targetMelodyRef = useRef(targetMelody);
  const onCompleteRef = useRef(onComplete);
  const onNoteMatchedRef = useRef(onNoteMatched);
  const onNoteDetectedRef = useRef(onNoteDetected);
  const onWrongConfirmedRef = useRef(onWrongConfirmed);

  const paramsRef = useRef({
    enableAutoSemitoneBias,
    toleranceCents,
    toleranceHz,
    detectionSensitivity,
    minRms,
    requireSilenceBetweenNotes,
    silenceFactor,
    silenceFramesRequired,
    maxRearmWaitMs,
    wrongStableFramesRequired,
    wrongCooldownMs,
    wrongCentsThreshold,
    wrongHzThreshold,
  });

  useEffect(() => { targetMelodyRef.current = targetMelody; }, [targetMelody]);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
  useEffect(() => { onNoteMatchedRef.current = onNoteMatched; }, [onNoteMatched]);
  useEffect(() => { onNoteDetectedRef.current = onNoteDetected; }, [onNoteDetected]);
  useEffect(() => { onWrongConfirmedRef.current = onWrongConfirmed; }, [onWrongConfirmed]);

  useEffect(() => {
    paramsRef.current = {
      enableAutoSemitoneBias,
      toleranceCents,
      toleranceHz,
      detectionSensitivity,
      minRms,
      requireSilenceBetweenNotes,
      silenceFactor,
      silenceFramesRequired,
      maxRearmWaitMs,
      wrongStableFramesRequired,
      wrongCooldownMs,
      wrongCentsThreshold,
      wrongHzThreshold,
    };
  }, [
    enableAutoSemitoneBias,
    toleranceCents,
    toleranceHz,
    detectionSensitivity,
    minRms,
    requireSilenceBetweenNotes,
    silenceFactor,
    silenceFramesRequired,
    maxRearmWaitMs,
    wrongStableFramesRequired,
    wrongCooldownMs,
    wrongCentsThreshold,
    wrongHzThreshold,
  ]);

  // Bias storage
  const BIAS_KEY = "tt_bias_cents_v2";
  const stored =
    (typeof window !== "undefined" && window.localStorage?.getItem(BIAS_KEY)) || "0";
  const biasCentsRef = useRef(Number(stored || 0));
  const biasVotesRef = useRef({ plus: 0, minus: 0 });

  useEffect(() => {
    if (tuningBiasCents !== null && biasCentsRef.current !== tuningBiasCents) {
      biasCentsRef.current = tuningBiasCents;
    }
  }, [tuningBiasCents]);

  const applyBias = useCallback((freq) => {
    const factor = Math.pow(2, (biasCentsRef.current || 0) / 1200);
    return freq * factor;
  }, []);

  // ✅ make these STABLE so deps don’t change every render
  const maybeLearnSemitoneBias = useCallback((rawFreq, expectedRaw) => {
    if (!paramsRef.current.enableAutoSemitoneBias) return;
    if (!expectedRaw || !hasOctave(expectedRaw)) return;
    if (!isFinite(rawFreq) || rawFreq <= 0) return;

    const midi = nameOctToMidi(expectedRaw);
    if (midi == null) return;
    const target = midiToFreq(midi);
    const cents = 1200 * Math.log2(rawFreq / target);

    if (Math.abs(cents + 100) < 40) biasVotesRef.current.minus++;
    else if (Math.abs(cents - 100) < 40) biasVotesRef.current.plus++;

    const votes = biasVotesRef.current;
    if (votes.minus >= 8 && biasCentsRef.current !== 100) {
      biasCentsRef.current = 100;
      try { window.localStorage?.setItem(BIAS_KEY, String(biasCentsRef.current)); } catch {}
      votes.minus = votes.plus = 0;
    } else if (votes.plus >= 8 && biasCentsRef.current !== -100) {
      biasCentsRef.current = -100;
      try { window.localStorage?.setItem(BIAS_KEY, String(biasCentsRef.current)); } catch {}
      votes.minus = votes.plus = 0;
    }
  }, []);

  const centsDiffToTarget = useCallback((pitchFreq, targetNote) => {
    if (!hasOctave(targetNote)) return null;
    const midi = nameOctToMidi(targetNote);
    if (midi == null) return null;
    const target = midiToFreq(midi);
    const f = foldHarmonicNearExpected(pitchFreq, targetNote);
    if (f == null) return null;
    return 1200 * Math.log2(f / target);
  }, []);

  const hzDiffToTarget = useCallback((pitchFreq, targetNote) => {
    if (!hasOctave(targetNote)) return null;
    const midi = nameOctToMidi(targetNote);
    if (midi == null) return null;
    const target = midiToFreq(midi);
    const f = foldHarmonicNearExpected(pitchFreq, targetNote);
    if (f == null) return null;
    return Math.abs(f - target);
  }, []);

  const isPitchCloseToTarget = useCallback((pitchFreq, targetNote) => {
    if (!isFinite(pitchFreq) || pitchFreq <= 0) return false;
    const f = foldHarmonicNearExpected(pitchFreq, targetNote);
    if (f == null) return false;

    const { toleranceCents, toleranceHz } = paramsRef.current;

    if (hasOctave(targetNote)) {
      const midi = nameOctToMidi(targetNote);
      if (midi == null) return false;
      const target = midiToFreq(midi);
      const centsDiff = 1200 * Math.log2(f / target);
      const hzDiff = Math.abs(f - target);
      return Math.abs(centsDiff) <= toleranceCents || hzDiff <= toleranceHz;
    }

    // pitch-class fallback
    const cls = normalizeNoteName(targetNote);
    const estMidi = freqToMidi(f);
    const baseOct = Math.round(estMidi / 12);
    for (let o = baseOct - 2; o <= baseOct + 2; o++) {
      const idx = NAMES_SHARP.indexOf(cls);
      if (idx < 0) continue;
      const guess = midiToFreq(o * 12 + idx);
      const cents = Math.abs(1200 * Math.log2(f / guess));
      const hz = Math.abs(f - guess);
      if (cents <= toleranceCents || hz <= toleranceHz) return true;
    }
    return false;
  }, []);

  const startNoteFromFreq = useCallback((freq, expectedRaw) => {
    const folded = foldHarmonicNearExpected(freq, expectedRaw);
    if (folded == null) return null;

    const midi = safeMidiFromFreq(folded);
    const { oct, full } = midiToNameOct(midi);

    return {
      className: normalizeNoteName(full),
      full,
      oct,
      freq: folded,
      snappedFreq: midiToFreq(midi),
    };
  }, []);

  function resetWrongTracker() {
    wrongNoteRef.current = null;
    wrongCountRef.current = 0;
  }

  const stopListening = useCallback(() => {
    try {
      if (processorRef.current) {
        processorRef.current.disconnect();
        processorRef.current.onaudioprocess = null;
        processorRef.current = null;
      }
      if (lpRef.current) { lpRef.current.disconnect(); lpRef.current = null; }
      if (hpRef.current) { hpRef.current.disconnect(); hpRef.current = null; }
      if (sourceRef.current) { sourceRef.current.disconnect(); sourceRef.current = null; }
    } catch {}

    try {
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((t) => t.stop());
        micStreamRef.current = null;
      }
    } catch {}

    try {
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        audioContextRef.current.close().catch(() => {});
      }
    } catch {}

    needsRearmRef.current = false;
    silenceFramesRef.current = 0;
    rearmStartedAtRef.current = 0;
    resetWrongTracker();
    lastWrongAtRef.current = 0;

    setIsListening(false);
  }, []);

  const startListening = useCallback(async () => {
    setErrorMsg("");
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!audioContextRef.current || audioContextRef.current.state === "closed") {
        audioContextRef.current = new AC({ latencyHint: "interactive" });
      }
      if (audioContextRef.current.state === "suspended") {
        await audioContextRef.current.resume();
      }

      if (isMobileUA && !isSecure) {
        throw new Error("Microphone requires HTTPS on mobile. Please open an https URL.");
      }

      const constraints = isMobileUA
        ? { echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: 1 }
        : { echoCancellation: true, noiseSuppression: true, autoGainControl: false, channelCount: 1 };

      const stream = await navigator.mediaDevices.getUserMedia({ audio: constraints });
      micStreamRef.current = stream;

      const ctx = audioContextRef.current;
      sourceRef.current = ctx.createMediaStreamSource(stream);

      detectPitchRef.current = pitchfinder.AMDF({ sampleRate: ctx.sampleRate });

      hpRef.current = ctx.createBiquadFilter();
      hpRef.current.type = "highpass";
      hpRef.current.frequency.value = 70;

      lpRef.current = ctx.createBiquadFilter();
      lpRef.current.type = "lowpass";
      lpRef.current.frequency.value = 1400;

      const bufferSize = 1024;
      processorRef.current = ctx.createScriptProcessor(bufferSize, 1, 1);

      const targetMs = 90;
      requiredStableFramesRef.current = Math.max(
        2,
        Math.round((targetMs / 1000) * (ctx.sampleRate / bufferSize))
      );

      stableNoteRef.current = null;
      stableCountRef.current = 0;

      needsRearmRef.current = false;
      silenceFramesRef.current = 0;
      rearmStartedAtRef.current = 0;
      resetWrongTracker();
      lastWrongAtRef.current = 0;

      const { detectionSensitivity, minRms } = paramsRef.current;
      const effectiveMinRms =
        detectionSensitivity && detectionSensitivity > 0 ? minRms / detectionSensitivity : minRms;

      processorRef.current.onaudioprocess = (event) => {
        const input = event.inputBuffer.getChannelData(0);
        const {
          requireSilenceBetweenNotes,
          silenceFactor,
          silenceFramesRequired,
          maxRearmWaitMs,
          wrongStableFramesRequired,
          wrongCooldownMs,
          wrongCentsThreshold,
          wrongHzThreshold,
        } = paramsRef.current;

        if (requireSilenceBetweenNotes && needsRearmRef.current) {
          const now = typeof performance !== "undefined" ? performance.now() : Date.now();
          const quietThreshold = effectiveMinRms * silenceFactor;

          if (rms(input) < quietThreshold) silenceFramesRef.current += 1;
          else silenceFramesRef.current = 0;

          const waitedTooLong =
            rearmStartedAtRef.current && now - rearmStartedAtRef.current > maxRearmWaitMs;

          if (silenceFramesRef.current >= silenceFramesRequired || waitedTooLong) {
            needsRearmRef.current = false;
            silenceFramesRef.current = 0;
            rearmStartedAtRef.current = 0;
            stableNoteRef.current = null;
            stableCountRef.current = 0;
            resetWrongTracker();
          } else {
            const level = rms(input);
            if (level < effectiveMinRms) return;
            const raw = detectPitchRef.current ? detectPitchRef.current(input) : null;
            if (!raw || !isFinite(raw) || raw <= 0) return;

            const expectedRaw = targetMelodyRef.current[currentIndexRef.current] || null;
            const biased = applyBias(raw);
            const live = startNoteFromFreq(biased, expectedRaw);
            if (live) onNoteDetectedRef.current(live.full);
            return;
          }
        }

        const level = rms(input);
        if (level < effectiveMinRms) {
          stableNoteRef.current = null;
          stableCountRef.current = 0;
          resetWrongTracker();
          return;
        }

        const raw = detectPitchRef.current ? detectPitchRef.current(input) : null;
        if (!raw || !isFinite(raw) || raw <= 0) {
          stableNoteRef.current = null;
          stableCountRef.current = 0;
          resetWrongTracker();
          return;
        }

        const expectedRaw = targetMelodyRef.current[currentIndexRef.current] || null;

        maybeLearnSemitoneBias(raw, expectedRaw);

        const biased = applyBias(raw);
        const live = startNoteFromFreq(biased, expectedRaw);
        if (!live) {
          stableNoteRef.current = null;
          stableCountRef.current = 0;
          resetWrongTracker();
          return;
        }

        onNoteDetectedRef.current(live.full);

        if (!expectedRaw) {
          stableNoteRef.current = null;
          stableCountRef.current = 0;
          resetWrongTracker();
          return;
        }

        const expectedClass = normalizeNoteName(expectedRaw);
        const liveClass = normalizeNoteName(live.full);

        const classMatches = liveClass === expectedClass;
        const pitchOk = classMatches && isPitchCloseToTarget(live.freq, expectedRaw);

        if (!pitchOk) {
          const cents = centsDiffToTarget(live.freq, expectedRaw);
          const hz = hzDiffToTarget(live.freq, expectedRaw);

          const clearlyWrong =
            liveClass !== expectedClass &&
            ((cents != null && Math.abs(cents) >= wrongCentsThreshold) ||
              (hz != null && hz >= wrongHzThreshold));

          if (clearlyWrong) {
            if (wrongNoteRef.current !== liveClass) {
              wrongNoteRef.current = liveClass;
              wrongCountRef.current = 1;
            } else {
              wrongCountRef.current += 1;
            }

            const now = typeof performance !== "undefined" ? performance.now() : Date.now();
            const cooled = now - (lastWrongAtRef.current || 0) >= wrongCooldownMs;

            if (wrongCountRef.current >= wrongStableFramesRequired && cooled) {
              lastWrongAtRef.current = now;

              onWrongConfirmedRef.current({
                detected: live.full,
                expected: expectedRaw,
                centsDiff: cents,
                hzDiff: hz,
              });

              resetWrongTracker();
            }
          } else {
            resetWrongTracker();
          }

          stableNoteRef.current = null;
          stableCountRef.current = 0;
          return;
        }

        resetWrongTracker();

        if (stableNoteRef.current !== expectedClass) {
          stableNoteRef.current = expectedClass;
          stableCountRef.current = 1;
        } else {
          stableCountRef.current++;
        }

        if (stableCountRef.current >= requiredStableFramesRef.current) {
          matchedRef.current.push(expectedRaw);
          onNoteMatchedRef.current([...matchedRef.current]);

          currentIndexRef.current += 1;
          forceUpdate((n) => n + 1);

          if (currentIndexRef.current >= (targetMelodyRef.current?.length || 0)) {
            stopListening();
            confetti({ particleCount: 150, spread: 90, origin: { y: 0.6 } });
            onCompleteRef.current();
            return;
          }

          stableNoteRef.current = null;
          stableCountRef.current = 0;

          if (paramsRef.current.requireSilenceBetweenNotes) {
            needsRearmRef.current = true;
            silenceFramesRef.current = 0;
            rearmStartedAtRef.current =
              typeof performance !== "undefined" ? performance.now() : Date.now();
          }
        }
      };

      sourceRef.current.connect(hpRef.current);
      hpRef.current.connect(lpRef.current);
      lpRef.current.connect(processorRef.current);
      processorRef.current.connect(ctx.destination);

      setIsListening(true);
    } catch (err) {
      console.error("❌ Error starting mic:", err);
      setErrorMsg(err?.message || "Failed to access the microphone.");
      stopListening();
    }
  }, [
    isMobileUA,
    isSecure,
    stopListening,
    applyBias,
    startNoteFromFreq,
    maybeLearnSemitoneBias,
    isPitchCloseToTarget,
    centsDiffToTarget,
    hzDiffToTarget,
  ]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
      return;
    }

    currentIndexRef.current = 0;
    matchedRef.current = [];
    stableNoteRef.current = null;
    stableCountRef.current = 0;

    needsRearmRef.current = false;
    silenceFramesRef.current = 0;
    rearmStartedAtRef.current = 0;
    resetWrongTracker();
    lastWrongAtRef.current = 0;

    forceUpdate((n) => n + 1);
    startListening();
  }, [isListening, startListening, stopListening]);

  useEffect(() => {
    return () => stopListening();
  }, [stopListening]);

  useEffect(() => {
    if (autoStart && !isListening) toggleListening();
  }, [autoStart, isListening, toggleListening]);

  return (
    <div className={`${containerClassName} mt-4`}>
      {showMicHint && (
        <p className="text-sm text-gray-600 mb-2 font-medium text-center">
          {isMobileUA ? "Use HTTPS on phone for mic access." : "Turn your mic on and click below"}
        </p>
      )}

      {!hideStartButton && (
        <div className={`w-full flex justify-center ${buttonWrapperClassName}`}>
          <button
            onClick={toggleListening}
            className={
              buttonClassName ||
              `${
                isListening ? "bg-red-500 hover:bg-red-600" : "bg-green-500 hover:bg-green-600"
              } text-white py-2 px-6 sm:px-10 rounded-full shadow-lg text-sm font-bold transition-all duration-300`
            }
          >
            {isListening ? `🛑 ${stopLabel}` : `🎤 ${startLabel}`}
          </button>
        </div>
      )}

      {errorMsg && <p className="text-xs text-red-600 mt-2 text-center">{errorMsg}</p>}

      <div className="mt-6 flex flex-wrap justify-center gap-3 w-full">
        {(targetMelody || []).map((note, idx) => {
          let style = "text-gray-600 border border-gray-300 bg-white";
          if (idx < currentIndexRef.current)
            style = "text-white bg-green-500 border-green-500 shadow-inner";
          else if (idx === currentIndexRef.current)
            style = "text-yellow-900 bg-yellow-200 border-yellow-400";

          return (
            <div
              key={`${note}-${idx}`}
              className={`w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full border text-xs sm:text-sm md:text-base ${style}`}
              title={note}
            >
              {note}
            </div>
          );
        })}
      </div>
    </div>
  );
}
